import { useState } from 'react';
import { enablePush, isIos, type PushChannel } from '../lib/push';
import { notifyPushChange, publishPushState, setPushDeniedAttempt, usePushStatus } from '../lib/push-status';
import { InstallButton } from './InstallButton';
import { Button, useToast } from './ui';

/**
 * Einmalige Einladung, Push einzuschalten.
 *
 * Die Einstellung dafuer steht weiter unten auf der Seite - gefunden wird sie
 * dort selten. Deshalb einmal oben fragen, mit einem Knopf, und nie wieder
 * drängeln: "Später" haelt ein halbes Jahr, auf diesem Geraet.
 */
const SPAETER_TAGE = 180;

export const GESPERRT_HINWEIS = 'Tippe in der Adresszeile auf das Schloss- oder Einstellungssymbol, setze „Benachrichtigungen“ auf „Erlauben“ und lade die Seite neu. Auf dem iPhone steht die Einstellung unter Einstellungen > Mitteilungen.';

function schluessel(channel: PushChannel): string {
  return `ukinfo:push-prompt:${channel}`;
}

/** Wurde die Einladung weggeklickt? Dann uebernimmt wieder die Karte. */
export function pushPromptVerborgen(channel: PushChannel): boolean {
  try {
    const bis = window.localStorage.getItem(schluessel(channel));
    return bis !== null && Number(bis) > Date.now();
  } catch {
    // Ohne Speicher (privates Fenster) lieber einmal zu viel fragen.
    return false;
  }
}

/** Einladung ausblenden - auch nach "Ausschalten" in der Karte, damit nicht gleich wieder gefragt wird. */
export function verbirgPushPrompt(channel: PushChannel): void {
  try {
    window.localStorage.setItem(schluessel(channel), String(Date.now() + SPAETER_TAGE * 86_400_000));
  } catch {
    // Ohne Speicher erscheint der Hinweis beim naechsten Mal erneut.
  }
  notifyPushChange();
}

export function PushPrompt({ channel }: { channel: PushChannel }): JSX.Element | null {
  const toast = useToast();
  const { state, deniedAttempt } = usePushStatus();
  const [busy, setBusy] = useState(false);

  // Nur fragen, wo es auch etwas zu tun gibt.
  const moeglich = (state === 'off' || state === 'needs-install') && !pushPromptVerborgen(channel);
  const blockiert = state === 'denied' && deniedAttempt;
  const aufIos = state === 'needs-install';

  if (blockiert) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
        <p className="font-semibold text-amber-900">Benachrichtigungen sind blockiert</p>
        <p className="mt-0.5 text-sm text-amber-900/80">{GESPERRT_HINWEIS}</p>
        <div className="mt-3">
          <Button variant="ghost" onClick={() => setPushDeniedAttempt(false)}>Verstanden</Button>
        </div>
      </div>
    );
  }
  if (!moeglich) return null;

  return (
    <div className="rounded-xl border border-brand-200 bg-brand-50 p-4">
      <p className="font-semibold text-brand-900">Nichts mehr verpassen?</p>
      <p className="mt-0.5 text-sm text-brand-900/80">
        {channel === 'link'
          ? 'Neue Umfragen und offene Rückmeldungen kommen dann direkt als Nachricht aufs Handy.'
          : 'Aufgaben, Umfragen und Erinnerungen kommen dann direkt als Nachricht.'}
        {aufIos && ` Auf ${isIos() ? 'dem iPhone' : 'diesem Gerät'} dafür einmal die App auf den Home-Bildschirm legen.`}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {aufIos ? (
          <InstallButton channel={channel} />
        ) : (
          <Button
            loading={busy}
            onClick={() => {
              setBusy(true);
              void enablePush(channel)
                .then((state) => {
                  publishPushState(state);
                  if (state === 'on') {
                    toast.show('Push ist eingeschaltet');
                  } else if (state === 'denied') {
                    setPushDeniedAttempt(true);
                  }
                })
                .catch(() => toast.show('Push konnte nicht eingeschaltet werden', 'error'))
                .finally(() => setBusy(false));
            }}
          >
            Benachrichtigungen einschalten
          </Button>
        )}
        <Button variant="ghost" onClick={() => verbirgPushPrompt(channel)}>Später</Button>
      </div>
    </div>
  );
}
